import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Col, Container, Form, Input, Label, Row } from 'reactstrap';
import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import { useParams } from 'react-router-dom';
import Select from "react-select";
import useReactSelectCustomStyle from '../../../../hooks/useReactSelectCustomStyle';
import { useDispatch, useSelector } from 'react-redux';
import { getCCFPointDetails } from '../../../../slices/ManageCCFPoints/getCCFPointDetailSlice';
import { getCCFFilterList } from '../../../../slices/ManageCCFPoints/getCCFFilterListSlice';
import Loader from '../../../../Components/Common/Loader';
import Error from '../../../../Components/Common/Error';
import { getCertificationList } from '../../../../slices/ManageCCFPoints/getCertificationListSlice';
import { editCCFPointsAPI, getCCFFiltersAPI, getCertificationListAPI } from '../../../../apiConfig/apiCall';
import { toast } from 'react-toastify';
import CreatableSelect from "react-select/creatable";

export default function EditCCFPoint() {
    const params = useParams();
    const dispatch = useDispatch();

    const { isLoading: isLoadingDetails, isError: isErrorDetails, data: ccfPointData } = useSelector(state => state.getCCFPointDetailSlice);
    const { isLoading: isLoadingFilters, isError: isErrorFilters, data: ccfFilterData } = useSelector(state => state.getCCFFilterListSlice);

    // Get CCF
    const [certificationListData, setCertificationListData] = useState([]);
    const [filteredCertificationData, setFilteredCertificationData] = useState({}); // New state for filtered points

    // State to track all checked certification points in a single array
    const [checkedCertifications, setCheckedCertifications] = useState([]);

    const getCertificationListData = async () => {
        try {
            const apiResponse = await getCertificationListAPI();
            if (apiResponse.status === 200) {
                setCertificationListData(apiResponse.data);
            }
        } catch (error) {
            console.log(error.message);
        }
    };

    const selectBoxCustomStyles = useReactSelectCustomStyle();

    const [domainTitle, setDomainTitle] = useState({ value: '', label: '' });
    const [controlTitle, setControlTitle] = useState({ value: '', label: '' });
    const [controlType, setControlType] = useState({ value: '', label: '' });
    const [department, setDepartment] = useState({ value: '', label: '' });
    const [controlReference, setControlReference] = useState('');
    const [controlWording, setControlWording] = useState('');
    const [controlArtifacts, setControlArtifacts] = useState('');
    const [controlNarrative, setControlNarrative] = useState('');

    useEffect(() => {
        if (ccfPointData) {
            setDomainTitle({ value: ccfPointData.domainTitle || '', label: ccfPointData.domainTitle || '' });
            setControlTitle({ value: ccfPointData.controlTitle || '', label: ccfPointData.controlTitle || '' });
            setControlReference(ccfPointData.controlReference || '');
            setControlType({ value: ccfPointData.controlType || '', label: ccfPointData.controlType || '' });
            setDepartment({ value: ccfPointData.department || '', label: ccfPointData.department || '' });
            setControlWording(ccfPointData.controlWording || '');
            setControlArtifacts(ccfPointData.controlArtifacts || '');
            setControlNarrative(ccfPointData.controlNarrative || '');
        }
        if (ccfPointData && ccfPointData.certificationVersionPoints) {
            const selectedPoints = ccfPointData.certificationVersionPoints.map(point => point.id);
            setCheckedCertifications(selectedPoints);
        }
    }, [ccfPointData]);

    useEffect(() => {
        dispatch(getCCFPointDetails(params.id));
        dispatch(getCCFFilterList());
        dispatch(getCertificationList());
        getCertificationListData();
    }, [dispatch, params.id]);

    if (isLoadingDetails || isLoadingFilters) return <Loader />;
    if (isErrorDetails || isErrorFilters) return <Error />;

    const domainTitleOptions = ccfFilterData?.DomainTitle?.map(title => ({ value: title, label: title })) || [];
    const controlTitleOptions = ccfFilterData?.ControlTitle?.map(title => ({ value: title, label: title })) || [];
    const controlTypeOptions = ccfFilterData?.ControlType?.map(type => ({ value: type, label: type })) || [];
    const departmentOptions = ccfFilterData?.Department?.map(dept => ({ value: dept, label: dept })) || [];

    const handleSearch = (e, certificationId, points) => {
        const searchTerm = e.target.value.toLowerCase();
        const filteredPoints = points.filter((point) =>
            point.controlWording.toLowerCase().includes(searchTerm)
        );
        setFilteredCertificationData((prevState) => ({
            ...prevState,
            [certificationId]: filteredPoints,
        }));
    };

    // Handle checkbox change to track all checked certification points in a single array
    const handleCheckboxChange = (pointId) => {
        setCheckedCertifications((prevState) => {
            if (prevState.includes(pointId)) {
                return prevState.filter(id => id !== pointId);
            } else {
                return [...prevState, pointId];
            }
        });
    };

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();

            const updatedData = {
                id: params.id,
                domainTitle: domainTitle?.value || '',
                controlTitle: controlTitle?.value || '',
                controlReference,
                controlType: controlType?.value || '',
                department: department?.value || '',
                controlWording,
                controlArtifacts,
                controlNarrative,
            };

            const apiResponse = await editCCFPointsAPI(updatedData, checkedCertifications);
            if (apiResponse.status === 200) {
                toast.success(apiResponse.data);
                dispatch(getCCFPointDetails(params.id));
            }
        } catch (error) {
            console.log(error.message);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title={'Edit CCF Point'} pageTitle={"Dashboard"} />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row className="gy-3">
                                        <Col lg={4}>
                                            <Label htmlFor="domainTitle" className="form-label">Domain Title</Label>
                                            <CreatableSelect
                                                name="domainTitle"
                                                value={domainTitle}
                                                onChange={(option) => setDomainTitle(option)}
                                                options={domainTitleOptions}
                                                styles={selectBoxCustomStyles}
                                                placeholder="Select or type domain title"
                                                isClearable
                                                onCreateOption={(inputValue) => {
                                                    if (inputValue.length > 100) {
                                                        toast.error('Domain Title cannot exceed 100 characters.');
                                                    } else {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setDomainTitle(newOption);
                                                        domainTitleOptions.push(newOption);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.target.value.length > 100) {
                                                        e.preventDefault();
                                                        toast.error('Domain Title cannot exceed 100 characters.');
                                                    }
                                                }}
                                                onBlur={(event) => {
                                                    const inputValue = event.target.value;
                                                    if (inputValue.length > 100) {
                                                        toast.error('Domain Title cannot exceed 100 characters.');
                                                    } else if (inputValue) {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setDomainTitle(newOption);
                                                        domainTitleOptions.push(newOption);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Label htmlFor="controlTitle" className="form-label">Control Title</Label>
                                            <CreatableSelect
                                                name="controlTitle"
                                                value={controlTitle}
                                                onChange={(option) => setControlTitle(option)}
                                                options={controlTitleOptions}
                                                styles={selectBoxCustomStyles}
                                                placeholder="Select control title"
                                                isClearable
                                                onCreateOption={(inputValue) => {
                                                    if (inputValue.length > 100) {
                                                        toast.error('Control Title cannot exceed 100 characters.');
                                                    } else {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setControlTitle(newOption);
                                                        controlTitleOptions.push(newOption);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.target.value.length > 100) {
                                                        e.preventDefault();
                                                        toast.error('Control Title cannot exceed 100 characters.');
                                                    }
                                                }}
                                                onBlur={(event) => {
                                                    const inputValue = event.target.value;
                                                    if (inputValue.length > 100) {
                                                        toast.error('Control Title cannot exceed 100 characters.');
                                                    } else if (inputValue) {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setControlTitle(newOption);
                                                        controlTitleOptions.push(newOption);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Label htmlFor="controlReference" className="form-label">Control Reference</Label>
                                            <Input
                                                type="text"
                                                id="controlReference"
                                                value={controlReference}
                                                onChange={(e) => setControlReference(e.target.value)}
                                                placeholder="Enter control reference"
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Label htmlFor="controlType" className="form-label">Control Type</Label>
                                             <CreatableSelect
                                                name="controlType"
                                                value={controlType}
                                                onChange={(option) => setControlType(option)}
                                                options={controlTypeOptions}
                                                styles={selectBoxCustomStyles}
                                                placeholder="Select control type"
                                                isClearable
                                                onCreateOption={(inputValue) => {
                                                    if (inputValue.length > 100) {
                                                        toast.error('Control Type cannot exceed 100 characters.');
                                                    } else {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setControlType(newOption);
                                                        controlTypeOptions.push(newOption);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.target.value.length > 100) {
                                                        e.preventDefault();
                                                        toast.error('Control Type cannot exceed 100 characters.');
                                                    }
                                                }}
                                                onBlur={(event) => {
                                                    const inputValue = event.target.value;
                                                    if (inputValue.length > 100) {
                                                        toast.error('Control Type cannot exceed 100 characters.');
                                                    } else if (inputValue) {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setControlType(newOption);
                                                        controlTypeOptions.push(newOption);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Label htmlFor="department" className="form-label">Department</Label>
                                             <CreatableSelect
                                                name="department"
                                                value={department}
                                                onChange={(option) => setDepartment(option)}
                                                options={departmentOptions}
                                                styles={selectBoxCustomStyles}
                                                placeholder="Select department"
                                                isClearable
                                                onCreateOption={(inputValue) => {
                                                    if (inputValue.length > 100) {
                                                        toast.error('Department cannot exceed 100 characters.');
                                                    } else {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setDepartment(newOption);
                                                        departmentOptions.push(newOption);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.target.value.length > 100) {
                                                        e.preventDefault();
                                                        toast.error('Department cannot exceed 100 characters.');
                                                    }
                                                }}
                                                onBlur={(event) => {
                                                    const inputValue = event.target.value;
                                                    if (inputValue.length > 100) {
                                                        toast.error('Department cannot exceed 100 characters.');
                                                    } else if (inputValue) {
                                                        const newOption = { value: inputValue, label: inputValue };
                                                        setDepartment(newOption);
                                                        departmentOptions.push(newOption);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col lg={4}></Col>
                                        <Col lg={4}>
                                            <Label htmlFor="controlWording" className="form-label">Control Wording</Label>
                                            <Input
                                                type="textarea"
                                                id="controlWording"
                                                value={controlWording}
                                                onChange={(e) => setControlWording(e.target.value)}
                                                placeholder="Enter control wording"
                                                style={{ height: '150px' }}
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Label htmlFor="controlArtifacts" className="form-label">Control Supporting Audit Artifacts/ RFI</Label>
                                            <Input
                                                type="textarea"
                                                id="controlArtifacts"
                                                value={controlArtifacts}
                                                onChange={(e) => setControlArtifacts(e.target.value)}
                                                placeholder="Enter control artifacts"
                                                style={{ height: '150px' }}
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Label htmlFor="controlNarrative" className="form-label">Control Narrative</Label>
                                            <Input
                                                type="textarea"
                                                id="controlNarrative"
                                                value={controlNarrative}
                                                onChange={(e) => setControlNarrative(e.target.value)}
                                                placeholder="Enter control narrative"
                                                style={{ height: '150px' }}
                                            />
                                        </Col>
                                        <Col lg={12}>
                                            <h4>Certifications</h4>
                                            <Row>
                                                {certificationListData.map((certification) => (
                                                    certification.certificationVersionPoints.length > 0 && (
                                                        <Col lg={3} key={certification.id} className="mb-3">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <Input
                                                                    type="checkbox"
                                                                    id={certification.certificationType.toLowerCase()}
                                                                    className="form-check-input me-2 rounded-circle"
                                                                />
                                                                <Label htmlFor={certification.certificationType.toLowerCase()} className="form-label mb-0">
                                                                    {certification.certificationType}
                                                                </Label>
                                                            </div>

                                                            <div className="w-75 mb-2">
                                                                <Input
                                                                    type="search"
                                                                    className="form-control"
                                                                    placeholder="Search type of filter"
                                                                    onChange={(e) => handleSearch(e, certification.id, certification.certificationVersionPoints)}
                                                                />
                                                            </div>

                                                            <div style={{ height: '10rem', overflowX: "hidden", overflowY: "scroll" }}>

                                                                {(filteredCertificationData[certification.id] || certification.certificationVersionPoints || []).map((point) => (
                                                                    <div className="form-check mb-2" key={point.id}>
                                                                        <Input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`formCheck${point.id}`}
                                                                            checked={checkedCertifications.includes(point.id)}  // This ensures the checkbox reflects the state
                                                                            onChange={() => handleCheckboxChange(point.id)}  // Call the handler to update state
                                                                        />
                                                                        <Label className="form-check-label" htmlFor={`formCheck${point.id}`}>
                                                                            {point.certificationIndex}
                                                                        </Label>
                                                                    </div>
                                                                ))}

                                                            </div>
                                                        </Col>
                                                    )
                                                ))}
                                            </Row>
                                        </Col>
                                        <Col lg={12}>
                                            <Button color="primary" type="submit" className="me-2">Update</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}